import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { getAdConfig } from '../lib/api';
import { AdBanner } from '../components/AdBanner';
import { 
  ChevronLeft, Search, ChefHat, Clock, Flame, 
  Coffee, Sun, Moon, Cookie, Loader2, UtensilsCrossed
} from 'lucide-react';

import { API_URL } from '../config/api';

export default function MieRicette() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  
  // Dynamic meal filters based on language
  const MEAL_FILTERS = [
    { id: 'all', labelKey: 'myRecipes.filters.all', icon: UtensilsCrossed },
    { id: 'colazione', labelKey: 'myRecipes.filters.breakfast', icon: Coffee },
    { id: 'pranzo', labelKey: 'myRecipes.filters.lunch', icon: Sun },
    { id: 'cena', labelKey: 'myRecipes.filters.dinner', icon: Moon },
    { id: 'spuntino', labelKey: 'myRecipes.filters.snack', icon: Cookie },
  ];
  
  // Meal type display names
  const getMealTypeName = (mealType) => {
    const names = {
      colazione: t('myRecipes.filters.breakfast'),
      pranzo: t('myRecipes.filters.lunch'),
      cena: t('myRecipes.filters.dinner'),
      spuntino: t('myRecipes.filters.snack')
    };
    return names[mealType] || mealType;
  };
  
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [adConfig, setAdConfig] = useState({ recipes_every_n_items: 4 });

  useEffect(() => {
    fetchRecipes();
    getAdConfig().then(res => setAdConfig(res.data)).catch(() => {});
  }, []);

  const fetchRecipes = async () => {
    try {
      // Use Bearer token explicitly — relying on cookies alone fails on
      // production because cross-domain cookies are blocked by the
      // browser (the API is on the same origin, but Safari WKWebView
      // and some Android wrappers strip session cookies on subdomain
      // navigation). Bearer is the canonical auth model app-wide.
      const sessionToken = localStorage.getItem('session_token') || localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/chef/my-recipes`, {
        credentials: 'include',
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setRecipes(data);
      } else {
        console.error('fetchRecipes failed:', res.status);
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter recipes
  const filteredRecipes = recipes.filter(recipe => {
    const matchesFilter = activeFilter === 'all' || recipe.meal_type === activeFilter;
    const matchesSearch = searchQuery === '' || 
      recipe.recipe_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recipe.recipe?.nome?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleRecipeClick = (recipe) => {
    // Navigate to recipe detail with recipe data
    navigate('/app/ricetta', { 
      state: { 
        recipe: recipe.recipe, 
        imageUrl: recipe.image_url 
      } 
    });
  };

  return (
    <div className={`${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} data-testid="mie-ricette-page">
      {/* Fixed Header - with safe area */}
      <div className={`fixed-header px-4 pb-4 ${isDark ? 'bg-[#080D1A]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <button 
            onClick={() => navigate(-1)}
            className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
            data-testid="btn-back"
          >
            <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <div className="flex-1">
            <h1 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('myRecipes.title')}
            </h1>
            <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('myRecipes.savedCount', { count: recipes.length })}
            </p>
          </div>
          <ChefHat className="w-8 h-8 text-[#EC4899]" />
        </div>

        {/* Search */}
        <div className={`relative mb-4`}>
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder={t('myRecipes.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className={`w-full pl-12 pr-4 py-3 rounded-xl ${
              isDark 
                ? 'bg-white/5 text-white placeholder-white/40 border-white/10' 
                : 'bg-gray-100 text-gray-900 placeholder-gray-400 border-gray-200'
            } border focus:outline-none focus:ring-2 focus:ring-[#EC4899]/50`}
          />
        </div>

        {/* Filter Pills - hidden scrollbar */}
        <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {MEAL_FILTERS.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeFilter === filter.id;
            return (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                data-testid={`filter-${filter.id}`}
                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all ${
                  isActive
                    ? 'bg-[#EC4899] text-white'
                    : isDark
                      ? 'bg-white/5 text-white/70 hover:bg-white/10'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t(filter.labelKey)}
              </button>
            );
          })}
        </div>
        </div>
      </div>

      {/* Content - with padding top for fixed header + extra breathing room */}
      <div className="content-below-header-xl pb-24 px-4">
        {/* Spacer for breathing room */}
        <div className="h-10"></div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#EC4899]" />
          </div>
        ) : filteredRecipes.length === 0 ? (
          <div className={`text-center py-20 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            <ChefHat className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium mb-2">
              {searchQuery || activeFilter !== 'all' 
                ? t('myRecipes.noResults') 
                : t('myRecipes.noRecipesYet')}
            </p>
            <p className="text-sm">
              {searchQuery || activeFilter !== 'all'
                ? t('myRecipes.tryDifferentFilter')
                : t('myRecipes.generateFirst')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* First ad banner at the top */}
            <AdBanner position="recipes" className="rounded-2xl" />
            
            {filteredRecipes.map((item, index) => (
              <React.Fragment key={item.recipe_id || index}>
                <button
                  onClick={() => handleRecipeClick(item)}
                  data-testid={`recipe-card-${index}`}
                  className={`w-full text-left rounded-2xl overflow-hidden transition-all ${
                    isDark 
                      ? 'bg-white/5 hover:bg-white/10' 
                      : 'bg-white hover:bg-gray-50 border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
                  }`}
                >
                  <div className="flex">
                    {/* Image */}
                    {item.image_url && (
                      <div className="w-28 h-28 flex-shrink-0">
                        <img 
                          src={item.image_url} 
                          alt={item.recipe?.nome || t('myRecipes.recipe')}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          {/* Meal type badge */}
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mb-1.5 ${
                            item.meal_type === 'colazione' ? 'bg-amber-500/20 text-amber-500' :
                            item.meal_type === 'pranzo' ? 'bg-orange-500/20 text-orange-500' :
                            item.meal_type === 'cena' ? 'bg-indigo-500/20 text-indigo-500' :
                            'bg-green-500/20 text-green-500'
                          }`}>
                            {getMealTypeName(item.meal_type)}
                          </span>
                          
                          {/* Recipe name */}
                          <h3 className={`font-semibold text-sm leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {item.recipe?.nome || item.recipe_name}
                          </h3>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-4 mt-2">
                        {item.recipe?.tempo_preparazione && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-[#3B82F6]" />
                            <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                              {item.recipe.tempo_preparazione}
                            </span>
                          </div>
                        )}
                        {item.recipe?.calorie && (
                          <div className="flex items-center gap-1">
                            <Flame className="w-3.5 h-3.5 text-[#E8334A]" />
                            <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                              {item.recipe.calorie} kcal
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Date */}
                      <p className={`text-xs mt-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        {new Date(item.created_at).toLocaleDateString(
                          language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US', 
                          { day: 'numeric', month: 'short', year: 'numeric' }
                        )}
                      </p>
                    </div>
                  </div>
                </button>
                {/* Insert ad after every N recipes */}
                {(index + 1) % (adConfig.recipes_every_n_items || 4) === 0 && (
                  <AdBanner position="recipes" className="rounded-2xl" />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
